import { Engine, Scene, Vector3, HavokPlugin, IDisposable } from '@babylonjs/core';
import { MainLight, MainCamera, ErrorMessageManager, Environment } from '@golf-ar/core';

import { Field } from './field';
import { GolfBall } from './golf-ball';
import { GolfBallTrajectoryManager } from './golf-ball-trajectory-manager';
import { Golfer } from './golfer';
import { BallTrail } from './ball-trail';
import { CollisionManager } from './collision-manager';
import { Ground } from './ground';
import { WinAnimation } from './win-animation';
import { GameManager } from './game-manager';

/** Visibility of auxiliary meshes. */
export const VISIBILITY_OF_AUXILIARY_MESHES = 0;
const ERROR_MESSAGE = 'Golf AR application is not supported on this mobile device. Please try to run it on other device.';

// Importing this engine via CDN, because npm package have some problems.
// See -https://forum.babylonjs.com/t/unable-to-load-havok-plugin-error-while-loading-wasm-file-from-browser/40289
// This version works OK, but we should keep in mind that we have to keep script with CDN in place.
// eslint-disable-next-line @typescript-eslint/naming-convention
declare const HavokPhysics: () => Promise<unknown>;

/** Base scene. */
export class BaseScene implements IDisposable {

	/** Register for browser/canvas resize events. */
	public readonly onResize: () => void;

	private readonly engine: Engine;

	private readonly scene: Scene;

	private readonly camera: MainCamera;

	private readonly light: MainLight;

	private readonly environment: Environment;

	private readonly field: Field;

	private readonly golfBall: GolfBall;

	private readonly golfer: Golfer;

	private readonly ballTrail: BallTrail;

	private readonly ground: Ground;

	private readonly errorMessageManager: ErrorMessageManager;

	private readonly winAnimationManager: WinAnimation;

	private readonly resources: IDisposable[] = [];

	/** Game manager. */
	public readonly gameManager: GameManager;

	public constructor(
		private readonly canvas: HTMLCanvasElement,
	) {
		this.engine = new Engine(this.canvas);
		this.scene = new Scene(this.engine);
		this.camera = new MainCamera(this.scene);
		this.light = new MainLight(this.scene);
		this.environment = new Environment(this.scene);
		this.field = new Field(this.scene);
		this.golfBall = new GolfBall(this.scene);
		this.ballTrail = new BallTrail(this.scene);
		this.golfer = new Golfer(this.scene, this.golfBall, this.field);
		this.ground = new Ground(this.scene, this.golfBall, this.field);
		this.gameManager = new GameManager(
			this.golfBall,
			this.golfer,
			this.field,
			this.scene,
			this.camera.resetPosition.bind(this.camera),
			this.ground.resetPosition.bind(this.ground),
		);
		this.errorMessageManager = new ErrorMessageManager();
		this.winAnimationManager = new WinAnimation(this.scene, this.gameManager);

		this.engine.enableOfflineSupport = false;
		this.engine.runRenderLoop(() => this.scene.render());

		this.scene.blockMaterialDirtyMechanism = true;

		this.onResize = this.engine.resize.bind(this.engine);

		this.createSceneElements();

		this.resources.push(
			this.engine,
			this.scene,
			this.camera,
			this.light,
			this.environment,
			this.field,
			this.golfBall,
			this.ballTrail,
			this.errorMessageManager,
			this.golfer,
		);
	}

	/** @inheritdoc */
	public dispose(): void {
		this.resources.forEach(resource => resource.dispose());
	}

	private async createSceneElements(): Promise<void> {
		this.gameManager.isLoading$.next(true);
		const physicsPlugin = await this.addPhysics();
		if (physicsPlugin === null) {
			return;
		}

		this.registerCollisionObserver(physicsPlugin);

		this.environment.create();
		await this.field.create();
		this.golfBall.create(this.field.startPosition);
		this.camera.lockToMesh(this.golfBall.instanceMesh);
		this.ballTrail.create(this.golfBall.instanceColliderMeshForTrail);
		await this.golfer.create(this.golfBall.instanceMesh.absolutePosition);
		await this.winAnimationManager.create(this.field.holePosition);

		this.ground.addChildren([
			this.golfBall.instanceMesh,
			this.field.instanceMesh,
			this.golfer.instanceParentGolferMesh,
			this.winAnimationManager.instanceMesh,
		]);

		const trajectoryManager = this.registerManagers();

		this.golfer.createGolferAnimationsEvent(trajectoryManager.startGenerateImpulse.bind(trajectoryManager));

		this.gameManager.isLoading$.next(false);
	}

	private registerManagers(): GolfBallTrajectoryManager {
		const trajectoryManager = new GolfBallTrajectoryManager(
			this.scene,
			this.camera.instance,
			this.camera,
			this.golfBall,
			this.ballTrail,
			this.golfer,
			this.winAnimationManager.instanceAnimation,
			this.gameManager.addEndStroke.bind(this.gameManager),
			this.gameManager.getIsEndGame.bind(this.gameManager),
		);

		this.resources.push(
			trajectoryManager,
		);

		return trajectoryManager;
	}

	private registerCollisionObserver(physicsPlugin: HavokPlugin): void {
		physicsPlugin.onCollisionObservable.add(collisionEvent => {
			CollisionManager.isCollisionComplete(
				this.golfBall.instancePhysicsBody,
				collisionEvent,
				this.gameManager.setIsBallInHole.bind(this.gameManager),
			);
		});
	}

	private createGravityConfiguration(): Vector3 {
		const gravity = -9.81;
		return new Vector3(0, gravity, 0);
	}

	private async addPhysics(): Promise<HavokPlugin | null> {
		try {
			const havokInstance = await HavokPhysics();
			const havokPlugin = new HavokPlugin(true, havokInstance);
			this.scene.enablePhysics(this.createGravityConfiguration(), havokPlugin);

			return havokPlugin;
		} catch {
			this.gameManager.isLoading$.next(false);
			this.errorMessageManager.displayErrorMessage(ERROR_MESSAGE);

			return null;
		}
	}
}
